import type { GalleryPhoto, TestimonialBeforeAfter, TestimonialGallery } from '@/data/testimonials';

type ReviewPhotosProps =
  | Pick<TestimonialGallery, 'layout' | 'photos'>
  | Pick<TestimonialBeforeAfter, 'layout' | 'beforeAfter'>;

const frameClassName = 'border-line bg-surface aspect-square overflow-hidden rounded border';
const imgClassName = 'block h-full w-full object-cover';

const Tile = ({ photo }: { photo: GalleryPhoto }) => (
  <figure className={frameClassName}>
    <img src={photo.src} alt={photo.alt} loading="lazy" decoding="async" className={imgClassName} />
  </figure>
);

const Label = ({ tone, children }: { tone: 'before' | 'after'; children: string }) => (
  <figcaption className="text-ink-soft mb-[7px] flex items-center gap-1.5 text-[10.5px] font-bold tracking-[.16em] uppercase">
    <span
      className={`h-1.5 w-1.5 rounded-full ${tone === 'before' ? 'bg-accent' : 'bg-primary'}`}
    />
    {children}
  </figcaption>
);

/**
 * Renders a testimonial's photo evidence — a 3-up thumbnail gallery, or a
 * labeled before/after pair — matching the approved reference treatment
 * (`docs/redesign/reference-testimonials-photos.html` `.gallery`/`.ba`).
 */
export const ReviewPhotos = (props: ReviewPhotosProps) => {
  if (props.layout === 'gallery') {
    return (
      <div className="mt-[22px] grid max-w-[560px] grid-cols-3 gap-2.5 md:gap-3.5">
        {props.photos.map((photo) => (
          <Tile key={photo.src} photo={photo} />
        ))}
      </div>
    );
  }

  const { before, after } = props.beforeAfter;

  return (
    <div className="mt-[22px] grid max-w-[560px] grid-cols-2 gap-3 md:gap-4">
      <figure>
        <Label tone="before">Before</Label>
        <div className={frameClassName}>
          <img
            src={before.src}
            alt={before.alt}
            loading="lazy"
            decoding="async"
            className={imgClassName}
          />
        </div>
      </figure>
      <figure>
        <Label tone="after">After</Label>
        <div className={frameClassName}>
          <img
            src={after.src}
            alt={after.alt}
            loading="lazy"
            decoding="async"
            className={imgClassName}
          />
        </div>
      </figure>
    </div>
  );
};
